import { useEffect } from 'react'
import { useClerk, useUser } from '@clerk/clerk-react'
import { useQueryClient } from '@tanstack/react-query'
import { settingsQueryKeys } from '@features/settings/api/queryKeys'
import { getUserPreferences } from '@features/settings/api/userPreferences.api'
import SelectorFooter from '@features/shared/components/SelectorFooter'
import { SignOutIcon } from '@features/shared/icons/icons'
import { UserAvatar } from '@features/shared/components/UserAvatar'
import { getUserInitials } from '@features/shared/utils/userInitials'
import { AccountPhotoEditor } from './AccountPhotoEditor'

function useSignedInUser() {
  const { user } = useUser()
  const email = user?.primaryEmailAddress?.emailAddress ?? null

  return {
    name: user?.fullName ?? null,
    email,
    initials: getUserInitials(user?.firstName, user?.lastName, email),
    // Uploaded photo only; Clerk's generated default is ignored.
    imageUrl: user?.hasImage ? user.imageUrl : null,
  }
}

/** The signed-in user's photo, or their initials: the Me item's icon on the rail. */
export function AccountAvatar() {
  const { initials, imageUrl } = useSignedInUser()

  return <UserAvatar imageUrl={imageUrl} initials={initials} size="sm" />
}

/** The signed-in user's name: the Me item's label on the rail ("Me" until it loads). */
export function AccountName() {
  const { name } = useSignedInUser()
  return <>{name ?? 'Me'}</>
}

/** Who is signed in, their name over their email: the Me menu's header. */
export function AccountIdentity({ onEditPhoto }: { onEditPhoto?: () => void }) {
  const { name, email } = useSignedInUser()
  const queryClient = useQueryClient()

  // The Me menu leads to Preferences, so its data starts loading as the menu opens.
  useEffect(() => {
    void queryClient.prefetchQuery({
      queryKey: settingsQueryKeys.userPreferences,
      queryFn: getUserPreferences,
    })
  }, [queryClient])

  return (
    <div className="flex min-w-0 flex-1 items-center gap-2.5 pr-2">
      <AccountPhotoEditor onEdit={onEditPhoto} />
      <div className="min-w-0 flex-1">
        <h2 className="truncate text-sm font-semibold leading-5 text-[color:var(--sidebar-submenu-text-default)]">
          {name ?? 'Me'}
        </h2>
        {email && (
          <p className="truncate text-xs leading-4 text-[color:var(--sidebar-submenu-text-faint)]">
            {email}
          </p>
        )}
      </div>
    </div>
  )
}

/** Sign out, at the foot of the Me menu. */
export function AccountSignOutFooter() {
  const { signOut } = useClerk()

  return (
    <SelectorFooter
      spacing="sidebar"
      actions={[
        {
          id: 'sign-out',
          label: 'Sign out',
          icon: <SignOutIcon size={12} aria-hidden="true" />,
          tone: 'danger',
          onClick: () => void signOut(),
        },
      ]}
    />
  )
}
